import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Note, Plus, Trash, PencilSimple, X, Bell, Users, User, Tag, FloppyDisk, BellRinging } from "@phosphor-icons/react";
import { useUnsavedGuard, loadDraft, clearDraft } from "../hooks/useUnsavedGuard";

const emptyNote = (user) => ({
  title: "", body: "", is_shared: false, reminder_at: "",
  tags: [], user_id: user.user_id, user_name: user.name,
});

const toLocalInput = (iso) => iso ? iso.slice(0, 16) : ""; // YYYY-MM-DDTHH:MM
const toIso = (local) => local ? (local.length === 16 ? local + ":00Z" : local) : null;

const Notes = ({ user, logout }) => {
  const [tab, setTab] = useState("personal"); // personal | shared
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // { form, original, draftKey, fromDraft, mode }
  const [dueReminders, setDueReminders] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/notes?scope=${tab}&user_id=${user.user_id}`);
      setNotes(data);
    } catch { toast.error("Failed to load notes"); }
    finally { setLoading(false); }
  }, [tab, user.user_id]);
  useEffect(() => { load(); }, [load]);

  // Poll reminders every 60s
  useEffect(() => {
    const fetchDue = async () => {
      try {
        const { data } = await axios.get(`${API}/notes/reminders/due?user_id=${user.user_id}`);
        setDueReminders(data);
        if (data.length > 0) {
          data.forEach(r => {
            if (!sessionStorage.getItem(`reminder-toast-${r.note_id}`)) {
              toast(`${r.title}`, { description: "Reminder is due!", icon: "🔔" });
              sessionStorage.setItem(`reminder-toast-${r.note_id}`, "1");
            }
          });
        }
      } catch (err) { console.debug("Reminder fetch failed (non-critical):", err); }
    };
    fetchDue();
    const h = setInterval(fetchDue, 60000);
    return () => clearInterval(h);
  }, [user.user_id]);

  const openCreate = () => {
    const draftKey = `draft-note-new`;
    const draft = loadDraft(draftKey);
    const base = emptyNote(user);
    if (draft) {
      toast.info("Loaded your unsaved draft");
      setEditor({ mode: "create", form: { ...base, ...draft }, original: base, draftKey, fromDraft: true });
    } else {
      setEditor({ mode: "create", form: base, original: base, draftKey, fromDraft: false });
    }
  };

  const openEdit = (note) => {
    const draftKey = `draft-note-edit-${note.note_id}`;
    const draft = loadDraft(draftKey);
    const base = {
      title: note.title || "", body: note.body || "", is_shared: !!note.is_shared,
      reminder_at: toLocalInput(note.reminder_at || ""), tags: note.tags || [],
      user_id: note.user_id, user_name: note.user_name,
    };
    if (draft) {
      toast.info("Loaded your unsaved draft");
      setEditor({ mode: "edit", form: { ...base, ...draft }, original: { ...base }, draftKey, fromDraft: true, note });
    } else {
      setEditor({ mode: "edit", form: base, original: { ...base }, draftKey, fromDraft: false, note });
    }
  };

  const saveNote = async () => {
    if (!editor) return;
    const payload = {
      ...editor.form,
      reminder_at: toIso(editor.form.reminder_at),
      tags: typeof editor.form.tags === "string"
        ? editor.form.tags.split(",").map(t => t.trim()).filter(Boolean)
        : editor.form.tags,
    };
    try {
      if (editor.mode === "create") {
        await axios.post(`${API}/notes`, payload);
        toast.success("Note created");
      } else {
        await axios.put(`${API}/notes/${editor.note.note_id}?requester_id=${user.user_id}`, payload);
        toast.success("Note updated");
      }
      clearDraft(editor.draftKey);
      setEditor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (note) => {
    if (!window.confirm(`Delete note "${note.title}"?`)) return;
    try {
      await axios.delete(`${API}/notes/${note.note_id}?requester_id=${user.user_id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const dismissReminder = async (note) => {
    try {
      await axios.put(`${API}/notes/${note.note_id}?requester_id=${user.user_id}`, { reminder_dismissed: true });
      toast.success("Reminder dismissed");
      setDueReminders(cur => cur.filter(r => r.note_id !== note.note_id));
      load();
    } catch { toast.error("Failed"); }
  };

  const editDirty = useMemo(() => {
    if (!editor) return false;
    const { form, original } = editor;
    const tagsStr = (x) => Array.isArray(x) ? x.join(",") : (x || "");
    return (
      form.title !== original.title ||
      form.body !== original.body ||
      form.is_shared !== original.is_shared ||
      form.reminder_at !== original.reminder_at ||
      tagsStr(form.tags) !== tagsStr(original.tags)
    );
  }, [editor]);

  const guard = useUnsavedGuard({
    isDirty: editDirty,
    draftKey: editor?.draftKey,
    draftData: editor?.form,
    onSaveDraft: () => { toast.success("Draft saved. It'll be here when you come back."); setEditor(null); },
    onDiscard: () => { setEditor(null); },
  });

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="sticky top-0 bg-white border-b border-slate-200 z-30 shadow-sm">
          <div className="px-8 py-5 flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Notes</h1>
              <p className="text-xs text-slate-500 uppercase tracking-[0.2em] mt-1">Personal & team notes with reminders</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {dueReminders.length > 0 && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-1.5 text-amber-800 text-sm" data-testid="reminders-banner">
                  <BellRinging size={16} weight="fill" className="animate-pulse" />
                  {dueReminders.length} reminder{dueReminders.length > 1 ? 's' : ''} due
                </div>
              )}
              <button data-testid="new-note-btn" onClick={openCreate}
                className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-medium flex items-center gap-2">
                <Plus size={16} weight="bold" /> New Note
              </button>
            </div>
          </div>
          <div className="px-8 flex border-b border-slate-100">
            {[
              { key: "personal", label: "My Notes", icon: User },
              { key: "shared", label: "Team Notes", icon: Users },
            ].map(t => {
              const I = t.icon;
              return (
                <button key={t.key} data-testid={`notes-tab-${t.key}`} onClick={() => setTab(t.key)}
                  className={`px-4 py-2.5 flex items-center gap-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                  <I size={14} weight={tab === t.key ? "fill" : "regular"} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-8">
          {dueReminders.length > 0 && (
            <div className="mb-6 grid gap-2" data-testid="due-reminders">
              {dueReminders.map(r => (
                <div key={r.note_id} className="flex items-center justify-between bg-amber-50 border-l-4 border-amber-400 px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-3">
                    <BellRinging size={22} weight="fill" className="text-amber-600" />
                    <div>
                      <div className="font-bold">{r.title}</div>
                      <div className="text-xs text-slate-500">{r.body?.slice(0, 80)}{r.body?.length > 80 ? '…' : ''}</div>
                      <div className="text-xs text-amber-700 mt-0.5">Reminder: {r.reminder_at?.replace("T", " ").slice(0, 16)}</div>
                    </div>
                  </div>
                  <button onClick={() => dismissReminder(r)} data-testid={`dismiss-reminder-${r.note_id}`}
                    className="text-sm font-medium text-amber-800 hover:bg-amber-100 px-3 py-1.5">Dismiss</button>
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-20"><div className="loading-spinner"></div></div>
          ) : notes.length === 0 ? (
            <div className="bg-white border border-slate-200 p-16 text-center" data-testid="notes-empty">
              <Note size={48} weight="duotone" className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No {tab} notes yet</p>
              <button onClick={openCreate} className="mt-4 text-sm text-[#002FA7] font-bold hover:underline">Create your first note →</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="notes-grid">
              {notes.map(n => {
                const isMine = n.user_id === user.user_id;
                const hasReminder = !!n.reminder_at;
                const reminderPassed = hasReminder && new Date(n.reminder_at) < new Date() && !n.reminder_dismissed;
                return (
                  <div key={n.note_id} data-testid={`note-card-${n.note_id}`}
                    className={`bg-white border shadow-sm p-5 hover:shadow-md transition ${reminderPassed ? 'border-amber-300' : 'border-slate-200'}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-bold text-slate-800 flex-1 break-words">{n.title}</h3>
                      {n.is_shared && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 border border-blue-200 flex items-center gap-1"><Users size={10} /> Team</span>}
                    </div>
                    {n.body && <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-6 mb-3">{n.body}</p>}
                    {hasReminder && (
                      <div className={`text-xs flex items-center gap-1 mb-2 ${reminderPassed ? 'text-amber-700 font-semibold' : 'text-slate-500'}`}>
                        <Bell size={12} weight={reminderPassed ? "fill" : "regular"} /> {n.reminder_at?.replace("T", " ").slice(0, 16)}
                      </div>
                    )}
                    {n.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {n.tags.map(t => (
                          <span key={t} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 flex items-center gap-1"><Tag size={10} />{t}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100 text-xs text-slate-400">
                      <span>By {n.user_name}</span>
                      {isMine && (
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(n)} data-testid={`edit-note-${n.note_id}`} className="p-1.5 hover:bg-blue-50 text-[#002FA7]" title="Edit"><PencilSimple size={14} /></button>
                          <button onClick={() => del(n)} data-testid={`delete-note-${n.note_id}`} className="p-1.5 hover:bg-red-50 text-red-500" title="Delete"><Trash size={14} /></button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editor && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 flex items-center justify-center p-4" data-testid="note-editor"
          onClick={() => guard.requestClose(() => setEditor(null))}>
          <div className="bg-white shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>{editor.mode === "create" ? "New Note" : "Edit Note"}</h3>
                {editor.fromDraft && <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-0.5 border border-amber-200 mt-1"><FloppyDisk size={12} /> Draft restored</span>}
                {editDirty && !editor.fromDraft && <span className="text-xs text-amber-600">• Unsaved changes</span>}
              </div>
              <button onClick={() => guard.requestClose(() => setEditor(null))} className="p-2 hover:bg-slate-100" data-testid="close-note-editor"><X size={18} /></button>
            </div>
            <form onSubmit={e => { e.preventDefault(); saveNote(); }} className="p-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Title</label>
                <input data-testid="note-title" value={editor.form.title}
                  onChange={e => setEditor({ ...editor, form: { ...editor.form, title: e.target.value } })}
                  className="rounded-none border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Body</label>
                <textarea data-testid="note-body" value={editor.form.body}
                  onChange={e => setEditor({ ...editor, form: { ...editor.form, body: e.target.value } })}
                  className="rounded-none border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" rows={6} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Reminder (optional)</label>
                  <input type="datetime-local" data-testid="note-reminder" value={editor.form.reminder_at || ""}
                    onChange={e => setEditor({ ...editor, form: { ...editor.form, reminder_at: e.target.value } })}
                    className="rounded-none border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Tags (comma-separated)</label>
                  <input data-testid="note-tags"
                    value={Array.isArray(editor.form.tags) ? editor.form.tags.join(", ") : (editor.form.tags || "")}
                    onChange={e => setEditor({ ...editor, form: { ...editor.form, tags: e.target.value } })}
                    placeholder="sales, follow-up, urgent"
                    className="rounded-none border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" data-testid="note-shared" checked={!!editor.form.is_shared}
                  onChange={e => setEditor({ ...editor, form: { ...editor.form, is_shared: e.target.checked } })} />
                <Users size={14} /> Share with team (visible to everyone)
              </label>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button type="button" onClick={() => guard.requestClose(() => setEditor(null))} className="rounded-none border border-slate-300 px-5 py-2.5 hover:bg-slate-50">Cancel</button>
                <button type="submit" data-testid="save-note" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-2.5 font-bold">Save Note</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Global UnsavedChangesModal rendered by NavigationGuardProvider */}
    </div>
  );
};

export default Notes;
